/**
 * Work that must finish while the session is still valid, run by an explicit
 * sign-out (AuthContext.logout) before the session is ended: e.g. the
 * mixer's debounced save (mixSync.ts), which would otherwise go out after the
 * sign-out and fail (401), or under the next account's cookie (refused, 409).
 *
 * A tiny registry so the auth code never imports the features that register
 * (the mixer loads lazily). Hooks are best effort: a failing
 * or slow hook never blocks the sign-out beyond {@link BEFORE_SIGN_OUT_TIMEOUT_MS}.
 */

export type BeforeSignOutHook = () => Promise<unknown> | unknown;

/** The longest a sign-out waits for its hooks. */
export const BEFORE_SIGN_OUT_TIMEOUT_MS = 3000;

const hooks = new Set<BeforeSignOutHook>();

/** Run `hook` before every explicit sign-out. Returns its unregister. */
export function onBeforeSignOut(hook: BeforeSignOutHook): () => void {
  hooks.add(hook);
  return () => { hooks.delete(hook); };
}

/** Runs every hook, at most `timeoutMs` in all. Never rejects. */
export async function runBeforeSignOut(timeoutMs: number = BEFORE_SIGN_OUT_TIMEOUT_MS): Promise<void> {
  if (hooks.size === 0) return;
  const all = Promise.allSettled([...hooks].map((hook) => {
    try {
      return Promise.resolve(hook());
    } catch (err) {
      return Promise.reject(err);
    }
  })).then((results) => {
    for (const result of results) {
      if (result.status === 'rejected') console.warn('[auth] a before-sign-out step failed:', result.reason);
    }
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => { timer = setTimeout(resolve, timeoutMs); });
  try {
    await Promise.race([all, timeout]);
  } finally {
    clearTimeout(timer);
  }
}
